import React, { useState, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../utils/AuthContext";
import { Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import HeroBg from "../data/App.jpg";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const API    = "https://api.komorotel.com";

function KomorotelLogo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      <circle cx="40" cy="40" r="38" fill={`${G}18`} />
      <path d="M22 55 Q15 40 22 27 Q30 15 42 14" stroke={G} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M22 55 Q28 63 38 64"              stroke={G} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q50 68 54 57"              stroke={G} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q35 58 40 54 Q45 50 42 44" stroke={G} strokeWidth="2"   strokeLinecap="round" fill="none"/>
      <rect x="39" y="12" width="7" height="48" rx="1.5" fill={G}/>
      <path d="M46 40 L64 24 L64 27 L52 40 L64 56 L61 56 L46 42 Z" fill={G}/>
      <circle cx="28" cy="28" r="2.5" fill={G}/>
      <circle cx="25" cy="36" r="2.5" fill={G}/>
      <circle cx="24" cy="44" r="2.5" fill={G}/>
      <circle cx="25" cy="52" r="2.5" fill={G}/>
    </svg>
  );
}

export default function Login() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { loading, error, dispatch } = useContext(AuthContext);

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [focused,  setFocused]  = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: "LOGIN_START" });
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      dispatch({ type: "LOGIN_SUCCESS", payload: res.data.user });
      if (location.state?.from === "checkout") {
        navigate(`/checkout?room=${location.state.roomId}&checkIn=${location.state.checkIn}&checkOut=${location.state.checkOut}`);
      } else {
        navigate("/");
      }
    } catch (err) {
      dispatch({ type: "LOGIN_ERROR", payload: err.response?.data });
    }
  };

  const inputStyle = (name) => ({
    width: "100%", border: `1.5px solid ${focused === name ? G : "#e5e7eb"}`,
    borderRadius: 12, padding: "12px 14px 12px 42px",
    fontSize: 14, outline: "none", fontFamily: "inherit",
    color: "#111827", background: "#fff",
    boxSizing: "border-box", transition: "border-color 0.2s",
  });

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>

      {/* ── Panneau gauche — image ── */}
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "flex-end" }} className="login-hero">
        <img src={HeroBg} alt="Comores" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,40,15,0.85) 0%, rgba(10,40,15,0.4) 50%, rgba(10,40,15,0.1) 100%)" }} />
        <div style={{ position: "relative", zIndex: 2, padding: "0 48px 56px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: G, color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", padding: "5px 14px", borderRadius: 20, marginBottom: 16 }}>
            🇰🇲 KOMOROTEL
          </div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 32, fontWeight: 700, color: "#fff", lineHeight: 1.2, marginBottom: 12 }}>
            L'archipel,<br />votre destination.
          </h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.7, maxWidth: 340 }}>
            Réservez des hébergements authentiques aux Comores — hôtels, lodges, maisons locales.
          </p>
        </div>
      </div>

      {/* ── Panneau droit — formulaire ── */}
      <div style={{ width: 480, background: "#fff", display: "flex", flexDirection: "column", justifyContent: "center", padding: "48px 48px" }} className="login-form-panel">

        {/* Logo */}
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", marginBottom: 40 }}>
          <KomorotelLogo size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "0.05em", color: G, lineHeight: 1.1 }}>KOMOROTEL</div>
            <div style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "0.03em", marginTop: 1 }}>L'archipel, votre destination.</div>
          </div>
        </Link>

        {/* Titre */}
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
          Bon retour 👋
        </h1>
        <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 32 }}>
          Connectez-vous à votre compte Komorotel
        </p>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Email */}
          <div style={{ position: "relative" }}>
            <Mail size={16} color={focused === "email" ? G : "#9ca3af"} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", transition: "color 0.2s" }} />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Adresse email"
              required
              style={inputStyle("email")}
              onFocus={() => setFocused("email")}
              onBlur={() => setFocused(null)}
            />
          </div>

          {/* Mot de passe */}
          <div style={{ position: "relative" }}>
            <Lock size={16} color={focused === "password" ? G : "#9ca3af"} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", transition: "color 0.2s" }} />
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe"
              required
              style={{ ...inputStyle("password"), paddingRight: 44 }}
              onFocus={() => setFocused("password")}
              onBlur={() => setFocused(null)}
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* Mot de passe oublié */}
          <div style={{ textAlign: "right", marginTop: -4 }}>
            <Link to="/forgot-password" style={{ fontSize: 12, color: G, fontWeight: 600, textDecoration: "none" }}>
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Erreur */}
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#ef4444", display: "flex", alignItems: "center", gap: 8 }}>
              <span>⚠️</span>{error.message || "Email ou mot de passe incorrect."}
            </div>
          )}

          {/* Bouton */}
          <button type="submit" disabled={loading}
            style={{
              width: "100%", background: loading ? "#d1d5db" : G,
              color: "#fff", border: "none", borderRadius: 12,
              padding: "14px 0", fontSize: 14, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              transition: "background 0.18s", marginTop: 4,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = G_DARK; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = G; }}>
            {loading ? "Connexion en cours..." : <><span>Se connecter</span><ArrowRight size={16} /></>}
          </button>

        </form>

        {/* Séparateur */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
          <span style={{ fontSize: 12, color: "#9ca3af" }}>ou</span>
          <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
        </div>

        {/* Inscription */}
        <p style={{ fontSize: 13, color: "#6b7280", textAlign: "center" }}>
          Pas encore de compte ?{" "}
          <Link to="/signup" style={{ color: G, fontWeight: 700, textDecoration: "none" }}>
            Créer un compte
          </Link>
        </p>

        {/* Footer */}
        <p style={{ fontSize: 11, color: "#d1d5db", textAlign: "center", marginTop: 32 }}>
          © 2025 Komorotel · Tous droits réservés
        </p>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .login-hero        { display: none !important; }
          .login-form-panel  { width: 100% !important; padding: 32px 24px !important; }
        }
      `}</style>
    </div>
  );
}